import { useNextStep } from 'nextstepjs';
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    CheckboxGroupOptions,
    CheckboxGroupVariants,
    checkboxRefusalCheckedIcon,
} from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import type { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import { AlertOutcomeTypes } from 'src/constants/AlertOutcomeTypes';
import { CareTypes, isSkilledNursingUnit } from 'src/constants/CareTypes';
import { Drawers } from 'src/constants/Drawers';
import {
    CareRefusalOptions,
    IndependentLivingResolutions,
    ResolutionCategoriesSorted,
    ResolutionTypes,
    SkilledNursingOptions,
} from 'src/constants/ResolutionTypes';
import { AlertTrainingTours } from 'src/constants/Training';
import useAlertTraining from 'src/hooks/useAlertTraining';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import { Resident } from 'src/store/sageAdminApi';

export interface SelectMultiOutcomeResolutionDrawerProps extends DrawerComponentProps {
    analyticsPrefix?: string;
    residentId?: string;
    hideAccidentallyPressed?: boolean;
    hideFoundDevice?: boolean;
    hideRoutineCheck?: boolean;
    task?: any;
    resident?: Resident;
    isCareRefusal?: boolean;
}

const DrawerName = 'Select Resolution Type Drawer';

function SelectMultiOutcomeResolutionDrawer(props: SelectMultiOutcomeResolutionDrawerProps) {
    const {
        analyticsPrefix,
        hasNextDrawer = false,
        hideAccidentallyPressed = false,
        hideFoundDevice = false,
        hideRoutineCheck = true,
        isCareRefusal = false,
        onNext,
        title = 'Select all care provided',
        task,
        resident,
    } = props;

    const { trackEvent } = useContext(AnalyticsContext);
    const { spacing, palette } = useTheme();
    const { isTrainingAlert, isGuidedTour } = useAlertTraining({ taskRid: task?.id });
    const { getFeatures } = useCareTypeFeatures();

    const isObservationsEnabledForCareType = getFeatures(
        task?.unit?.careType || resident?.unit?.careType
    ).observations.isEnabled;

    const isUnitIndependentLiving =
        task?.unit?.careType === CareTypes.IndependentLiving ||
        task?.unit?.ward === CareTypes.IndependentLiving;
    const hasResident = task?.unit?.currentOccupants?.length > 0 || !!resident;
    const useSkilledNursingOptions = isSkilledNursingUnit(task?.unit || resident?.unit);

    const titleToUse = useMemo(
        () => (useSkilledNursingOptions ? 'Select all care needs' : title),
        [useSkilledNursingOptions, title]
    );

    const { startNextStep } = useNextStep();

    const shouldShowObservationsDrawer = useMemo(
        () => isObservationsEnabledForCareType && hasResident,
        [isObservationsEnabledForCareType, hasResident]
    );

    // Determine which resolution types to show based on filters + care type
    const filteredResolutionOptions = useMemo(() => {
        const resolutionsForCareType = isCareRefusal
            ? CareRefusalOptions
            : isUnitIndependentLiving
            ? IndependentLivingResolutions
            : useSkilledNursingOptions
            ? SkilledNursingOptions
            : ResolutionCategoriesSorted;
        return resolutionsForCareType.map((category) => ({
            displayName: category.displayName,
            resolutionTypes:
                category.subTypes?.filter((type) => {
                    if (
                        hideAccidentallyPressed &&
                        type.id === ResolutionTypes.AccidentallyPressed.id
                    ) {
                        return false;
                    }

                    if (hideRoutineCheck && type.id === ResolutionTypes.RoutineCheck.id) {
                        return false;
                    }

                    return true;
                }) || [],
        }));
    }, [
        isCareRefusal,
        isUnitIndependentLiving,
        useSkilledNursingOptions,
        hideAccidentallyPressed,
        hideRoutineCheck,
    ]);

    const [selectedResolutions, setSelectedResolutions] = useState<Record<string, boolean>>({});
    const selectedResolutionsSet = useMemo(
        () =>
            new Set(
                Object.keys(selectedResolutions).filter(
                    (resolutionId) => selectedResolutions[resolutionId]
                )
            ),
        [selectedResolutions]
    );

    const isTestAlertSelected = useMemo(
        () => selectedResolutions[ResolutionTypes.FoundDevice.id],
        [selectedResolutions]
    );

    const selectedResolutionsArray = isTestAlertSelected
        ? [ResolutionTypes.FoundDevice.id]
        : Array.from(selectedResolutionsSet);

    const careOutcomes = selectedResolutionsArray.map((careProvided) => ({
        outcome: isCareRefusal ? 'REFUSED' : 'COMPLETED',
        careProvided,
    }));

    const numberResolutionsToSumbit = useMemo(
        () => (isTestAlertSelected ? 1 : selectedResolutionsSet.size),
        [isTestAlertSelected, selectedResolutionsSet]
    );

    const handleSelectResolution = (resolutionId: string, isChecked: boolean) => {
        trackEvent('Resolution Type Clicked', {
            resolutionId,
            isChecked,
            objectType: ObjectTypes.Checkbox,
            context: DrawerName,
        });
        setSelectedResolutions((prevSelectedOptions) => ({
            ...prevSelectedOptions,
            [resolutionId]: isChecked,
        }));
    };

    const handleClearAll = () => {
        trackEvent('Clear All Clicked', {
            context: DrawerName,
            objectType: ObjectTypes.Button,
            numResolutionsCleared: selectedResolutionsSet.size,
        });

        setSelectedResolutions({});
    };

    const handleSelectButton = (e: SyntheticEvent) => {
        e.preventDefault();

        trackEvent('Select Button Clicked', {
            objectType: ObjectTypes.Button,
            context: DrawerName,
            taskId: task?.id,
            numResolutionsSelected: selectedResolutionsSet.size,
            isSkilledNursing: useSkilledNursingOptions,
            isTestAlertSelected,
            isTrainingAlert,
        });

        onNext(
            {
                analyticsPrefix,
                careOutcomes,
                alertOutcomeType: isTestAlertSelected
                    ? AlertOutcomeTypes.TestAlert
                    : AlertOutcomeTypes.ResidentInteraction,
                isEmergency: task?.severity === 'EMERGENCY' || false,
                requireSelection: false,
            },
            hasNextDrawer
                ? undefined
                : shouldShowObservationsDrawer
                ? {
                      type: Drawers.AddObservations,
                      fullHeight: true,
                  }
                : { type: Drawers.ResolveTask }
        );
    };

    useEffect(() => {
        if (isTrainingAlert && isGuidedTour) {
            const timeout = setTimeout(() => {
                startNextStep(AlertTrainingTours.AlertTrainingSelectMultiOutcome);
            }, 500);

            return () => clearTimeout(timeout);
        }
    }, [isTrainingAlert, isGuidedTour, startNextStep]);

    return (
        <>
            <DrawerTitle sx={{ p: 0, gap: 2 }}>
                <Box sx={{ px: 2.5, pt: 9 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                        {titleToUse}
                    </Typography>
                </Box>
                <Divider />
            </DrawerTitle>
            <DrawerBody sx={{ p: 0 }}>
                <Stack divider={<Divider />}>
                    {filteredResolutionOptions.map((resolutionCategory, index) => (
                        <Stack
                            key={resolutionCategory.displayName || `no-category-${index}`}
                            sx={{ p: 2.5 }}
                        >
                            {resolutionCategory.displayName && (
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        bgcolor: 'grey.50',
                                        py: 1.5,
                                        mx: -2.5,
                                        px: 2.5,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        component="h2"
                                        sx={{ textTransform: 'uppercase' }}
                                    >
                                        {resolutionCategory.displayName}
                                    </Typography>
                                </Box>
                            )}

                            <CheckboxGroupOptions
                                options={resolutionCategory.resolutionTypes.map(
                                    (resolutionType) => ({
                                        value: resolutionType.id,
                                        highlighted:
                                            // apply different styles if `Needs Nurse` is selected
                                            resolutionType.id === ResolutionTypes.Medtech.id &&
                                            useSkilledNursingOptions,
                                        label: (
                                            <Stack
                                                direction="row"
                                                sx={{ alignItems: 'center', gap: 1.5 }}
                                            >
                                                {resolutionType.icon && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: spacing(5),
                                                            height: spacing(5),
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={resolutionType.icon}
                                                            color={
                                                                resolutionType.category?.id ===
                                                                'EMERGENCY'
                                                                    ? palette.error.main
                                                                    : palette.common.black
                                                            }
                                                            size="2x"
                                                            fixedWidth
                                                            style={{
                                                                opacity: !isTestAlertSelected
                                                                    ? 1
                                                                    : 0.3,
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: isTestAlertSelected
                                                            ? 'grey.300'
                                                            : 'common.black',
                                                        flex: '1',
                                                    }}
                                                >
                                                    {resolutionType.displayName}
                                                </Typography>
                                            </Stack>
                                        ),
                                        disabled: isTestAlertSelected,
                                    })
                                )}
                                onSelectOption={handleSelectResolution}
                                selectedOptions={selectedResolutions}
                                variant={CheckboxGroupVariants.Outlined}
                                checkboxProps={
                                    isCareRefusal
                                        ? {
                                              checkedIcon: checkboxRefusalCheckedIcon,
                                          }
                                        : undefined
                                }
                                formGroupProps={{
                                    'aria-label': 'Select Care Outcomes From List',
                                }}
                            />
                        </Stack>
                    ))}

                    {/* Test Alert */}
                    {!hideFoundDevice && (
                        <Stack sx={{ p: 2.5, pb: 4, gap: 2.5, bgcolor: 'grey.100' }}>
                            <Stack sx={{ gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    System Test
                                </Typography>
                                <Typography variant="subtitle1">
                                    A resident didn&apos;t set off this alert and nobody needs help.
                                </Typography>
                            </Stack>
                            <CheckboxGroupOptions
                                variant={CheckboxGroupVariants.Outlined}
                                selectedOptions={selectedResolutions}
                                options={[
                                    {
                                        value: ResolutionTypes.FoundDevice.id,
                                        label: ResolutionTypes.FoundDevice.displayName,
                                    },
                                ]}
                                onSelectOption={handleSelectResolution}
                                formGroupProps={{
                                    'aria-label': 'Test Alert Check Box',
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
            </DrawerBody>
            <DrawerFooter showShadow sx={{ bgcolor: 'common.white' }}>
                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={handleClearAll}
                        disableFocusRipple
                        disableElevation
                        disabled={selectedResolutionsSet.size === 0}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Clear All
                        </Typography>
                    </Button>
                    <Button
                        fullWidth
                        color="primary"
                        type="submit"
                        variant="contained"
                        onClick={handleSelectButton}
                        disableFocusRipple
                        disableElevation
                        disabled={selectedResolutionsSet.size === 0}
                    >
                        {selectedResolutionsSet.size <= 0
                            ? 'Select'
                            : 'Select (' + numberResolutionsToSumbit + ')'}
                    </Button>
                </Stack>
            </DrawerFooter>
        </>
    );
}

export default SelectMultiOutcomeResolutionDrawer;
